"use client";

import { Mail, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What is hotel revenue management?",
    answer: "Hotel revenue management is the strategic application of data-driven analytics to predict consumer behavior and optimize product availability and price to maximize revenue growth. It involves managing inventory, distribution channels, and pricing strategies to ensure the right room is sold to the right guest at the right time for the right price."
  }, {
    question: "How can OTA strategies improve my hotel's revenue?",
    answer: "Optimizing your OTA (Online Travel Agency) listings ensures higher visibility, better conversion rates, and improved ranking. By managing content, images, and rate parity effectively, we help you capture more bookings while maintaining control over your brand identity and profitability."
  }, {
    question: "Why is a hotel tech stack audit important?",
    answer: "A modern tech stack is the backbone of efficient operations. An audit identifies inefficiencies in your PMS, channel manager, and booking engine, ensuring your systems are integrated, scalable, and capable of supporting automated revenue management processes."
  }, {
    question: "What does a revenue audit include?",
    answer: "Our comprehensive revenue audit analyzes your historical performance, current market positioning, competitor pricing, and distribution health. We identify missed opportunities and provide actionable recommendations to boost your RevPAR and overall profitability."
  }, {
    question: "How do you handle rate parity issues?",
    answer: "Rate parity is crucial for maintaining brand integrity and direct booking growth. We monitor your rates across all channels in real-time, identifying discrepancies and providing the technical guidance needed to correct them and ensure your direct website remains the most competitive option."
  }, {
    question: "Can you help with direct booking growth?",
    answer: "Yes. We implement strategies to shift bookings from high-commission OTAs to your direct website. This includes optimizing your booking engine, enhancing website conversion paths, and leveraging digital marketing to attract high-value guests directly."
  }, {
    question: "What is the role of dynamic pricing?",
    answer: "Dynamic pricing involves adjusting your room rates in real-time based on demand, competitor activity, and market trends. This ensures you are never leaving money on the table during high demand and remain attractive during low-demand periods."
  }, {
    question: "How do you support hotels in different locations?",
    answer: "As a remote-first consultancy, we leverage cloud-based tools and deep industry expertise to manage revenue for properties globally. Our data-driven approach allows us to understand local market nuances and apply global best practices to your specific property."
  }, {
    question: "What makes HotelRevBaBa different?",
    answer: "We combine deep technical expertise with a hands-on, personalized approach. Led by Sunil Singh, we don't just provide reports; we act as an extension of your team, implementing the strategies and tech solutions necessary to drive sustainable revenue growth."
  }, {
    question: "How do I get started with a consultation?",
    answer: "Getting started is simple. You can book a free initial revenue audit or contact us directly through our website. We will review your current setup and discuss how our tailored revenue management strategies can help you achieve your financial goals."
  }];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  return <section data-section-id="516" id="propertymanagementfaq" className="bg-background py-32 text-foreground">
      <div className="container mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-8 bg-secondary" />
                <span className="font-default text-xs uppercase tracking-widest text-secondary">
                  Expert Insights
                </span>
              </div>

              <h2 className="font-default text-foreground text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.05] tracking-tight mb-6">
                Revenue
                <br />
                Management
                <br />
                FAQs.
              </h2>

              <p className="font-default text-muted-foreground text-base sm:text-lg leading-relaxed mb-10 max-w-md">
                Answers to common questions about maximizing your hotel's revenue and optimizing your digital strategy.
              </p>

              <div className="border border-border bg-card/50 rounded-lg p-6 max-w-md relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-secondary" />
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10 shrink-0">
                    <Mail className="h-4 w-4 text-secondary" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="font-default text-foreground text-lg mb-2 leading-tight">
                      Need custom advice?
                    </p>
                    <Link to="/ContactPage" className="font-default text-sm text-foreground inline-flex items-center gap-1.5 group/link border-b border-foreground/30 hover:border-secondary hover:text-secondary transition-colors duration-300 pb-0.5">
                      Contact us
                      <span className="transition-transform duration-300 group-hover/link:translate-x-1">
                        &rarr;
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border last:border-b-0 data-[state=open]:bg-accent/40 transition-colors duration-300 rounded-lg px-2 sm:px-4 -mx-2 sm:-mx-4">
                  <AccordionTrigger className="hover:no-underline py-6 sm:py-7 group/trigger [&>svg]:hidden">
                    <span className="font-default text-foreground text-lg sm:text-xl lg:text-[1.375rem] leading-snug pr-6 text-left flex-1">
                      {faq.question}
                    </span>
                    <span className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-all duration-500 group-data-[state=open]/trigger:rotate-[135deg] group-data-[state=open]/trigger:bg-secondary group-data-[state=open]/trigger:text-white group-data-[state=open]/trigger:border-secondary">
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-7 pr-12 sm:pr-16">
                    <p className="font-default text-muted-foreground text-base sm:text-[17px] leading-[1.7]">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />
    </section>;
}